'use client';

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useShallow } from 'zustand/react/shallow';
import { useJobQueueStore } from '@/features/design/job-queue';
import { useConceptStore } from '@/features/design/concept/model/concept-editor-store';
import { usePlan } from '@/features/subscription/model/subscription.selectors';
import { RenderStatus } from '@/shared/constants';
import { isUserVisibleRenderType } from '@/types/render';
import {
  useConceptAlbumSettings,
  type AlbumSectionToggles,
} from '@/features/design/album';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import { useRendersForConcept } from '@/features/design/render/model/renders-cache';
import { useAlbum } from '../hooks/use-album';
import { useAlbumEstimatePicker } from './use-album-estimate-picker';
import type { Album } from '../api/album-service';
import type { AlbumJob } from '@/features/design/job-queue';

export interface AlbumPanelAngle {
  id: string;
  attributes: { type: string };
  relationships?: unknown;
}

export interface UseAlbumPanelOptions {
  conceptId: string;
  conceptAngles: AlbumPanelAngle[];
  /** Project the concept belongs to — required to list the concept's estimates
   *  for the album Estimate picker (FE4). When absent the picker shows only
   *  "Generate new". */
  projectId?: string;
}

export function useAlbumPanel(options: UseAlbumPanelOptions) {
  const { conceptId, conceptAngles, projectId } = options;

  const queryClient = useQueryClient();
  const plan = usePlan();

  // Extract plan attributes
  const planData = plan && 'attributes' in plan ? plan.attributes : plan;
  const actualPlan = planData;

  const hasAdvancedAlbumOptions =
    actualPlan?.is_album_advanced_options_available ?? false;
  // Designer-tier access to premium album DESIGN templates (architect/studio).
  // Used ONLY to pick the locked-row badge (DESIGNER vs "Coming soon"), never
  // as a gate — the templates stay locked for everyone (Safe Net).
  const isAlbumTemplatesAvailable =
    actualPlan?.is_album_templates_available ?? false;

  // State
  const [highlightedAlbumId, setHighlightedAlbumId] = useState<string | null>(
    null
  );
  const [selectedLanguage, setSelectedLanguage] = useState<string>('en');

  // Refs
  const highlightTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastProcessedAlbumIdRef = useRef<string | null>(null);

  // Check if there's an active album generation in the queue
  const jobQueue = useJobQueueStore((state) => state.jobQueue);
  const albumInQueue = jobQueue.find(
    (item) => item.conceptId === conceptId && item.jobType === 'album'
  );
  const hasAlbumInQueue = !!(
    albumInQueue &&
    (albumInQueue.status === RenderStatus.PENDING ||
      albumInQueue.status === RenderStatus.PROCESSING)
  );

  // Get selected render IDs from concept store
  const selectedRenderIds = useConceptStore(
    (state) => state.ui.selectedRenderId
  );

  // Renders are owned by TanStack Query (`cacheKeys.renders.list(conceptId)`).
  const rendersForConcept = useRendersForConcept(conceptId);

  // Calculate selected renders for album generation
  const selectedRenders = useMemo(() => {
    const result: Record<string, string> = {};
    conceptAngles.forEach((angle) => {
      const completedRenders = rendersForConcept
        .filter(
          (r) =>
            r.relationships?.concept_angle?.data?.id === angle.id &&
            r.attributes.status === RenderStatus.COMPLETED &&
            isUserVisibleRenderType(r.attributes.render_type)
        )
        .sort(
          (a, b) =>
            new Date(a.attributes.created_at).getTime() -
            new Date(b.attributes.created_at).getTime()
        );

      if (completedRenders.length > 0) {
        const selectedRenderId = selectedRenderIds[angle.id];
        const selectedRender = selectedRenderId
          ? completedRenders.find((r) => r.id === selectedRenderId) ||
            completedRenders[completedRenders.length - 1]
          : completedRenders[completedRenders.length - 1];

        if (selectedRender) {
          result[angle.id] = selectedRender.id;
        }
      }
    });
    return result;
  }, [conceptAngles, rendersForConcept, selectedRenderIds]);

  // Thumbnail URLs for the exact renders the album will use — resolved from the
  // selected-render map so the strip matches `selected_renders` 1:1.
  const selectedRenderUrls = useMemo(() => {
    return Object.values(selectedRenders)
      .map((renderId) => {
        const render = rendersForConcept.find((r) => r.id === renderId);
        return (
          render?.relationships?.file?.meta?.url ?? render?.attributes.file_url
        );
      })
      .filter((url): url is string => Boolean(url));
  }, [selectedRenders, rendersForConcept]);

  // Cost-estimate picker (FE4) — project-scoped list with this design's
  // estimates first (default) and sibling designs' estimates as greyed tail
  // options. `null` selection = "New estimate" (generated in the album
  // pipeline). All defaulting/stale-guard logic lives in the shared hook.
  const estimatePicker = useAlbumEstimatePicker(projectId ?? '', conceptId);

  // Album settings from Zustand store
  const albumSettings = useConceptAlbumSettings(conceptId);
  const {
    selectedTemplate,
    selectedLanguage: storedSelectedLanguage,
    requirements: storedRequirements,
    updateSetting,
  } = albumSettings;

  // Content-section toggles (materials / blueprints / preparation guide /
  // safety requirements / building regulations) — all default ON.
  const storedSectionToggles: AlbumSectionToggles = {
    includeMaterials: albumSettings.includeMaterials,
    includeBlueprints: albumSettings.includeBlueprints,
    includePreparationGuide: albumSettings.includePreparationGuide,
    includeSafetyRequirements: albumSettings.includeSafetyRequirements,
    includeBuildingRegulations: albumSettings.includeBuildingRegulations,
  };

  // Album generation callback
  const handleAlbumComplete = useCallback(
    (_downloadUrl: string, albumId?: string) => {
      if (albumId) {
        if (highlightTimerRef.current) {
          clearTimeout(highlightTimerRef.current);
        }

        setHighlightedAlbumId(albumId);

        highlightTimerRef.current = setTimeout(() => {
          setHighlightedAlbumId(null);
          highlightTimerRef.current = null;
        }, 5000);
      }
    },
    []
  );

  // Album API hook
  const {
    albums,
    generateAlbum,
    isGenerating: isGeneratingAlbum,
    lastGenerateResult,
    resetGenerateResult,
    deleteAlbum,
    isDeleting,
    downloadAlbum,
  } = useAlbum({
    conceptId,
  });

  const isGenerating = isGeneratingAlbum || hasAlbumInQueue;

  // Subscribe to album jobs from Store
  const albumJobsFromQueue = useJobQueueStore(
    useShallow((state) => {
      const albums = state.getAllAlbumsForConcept?.(conceptId) || [];
      return albums;
    })
  );

  // Subscribe to recently completed albums
  const recentlyCompletedAlbums = useJobQueueStore(
    useShallow((state) => state.getRecentlyCompletedAlbums?.(conceptId) || [])
  );

  // Highlight newest completed album
  useEffect(() => {
    const latestCompleted = recentlyCompletedAlbums[0];
    if (
      latestCompleted?.id &&
      latestCompleted.id !== lastProcessedAlbumIdRef.current
    ) {
      lastProcessedAlbumIdRef.current = latestCompleted.id;
      handleAlbumComplete('', latestCompleted.id);
      queryClient.invalidateQueries({ queryKey: cacheKeys.albums.list(conceptId) });
    }
  }, [recentlyCompletedAlbums, handleAlbumComplete, conceptId, queryClient]);

  // Sync local state from store (store → local)
  // Reverse sync (local → store) handled by onLanguageChange callback
  useEffect(() => {
    setSelectedLanguage(storedSelectedLanguage);
  }, [storedSelectedLanguage]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (highlightTimerRef.current) {
        clearTimeout(highlightTimerRef.current);
      }
    };
  }, []);

  // Merge albums from job queue + API query.
  // The API album is the source of truth for completed-state metadata
  // (album_title, file_size, page_count, generated_at, public_url) — those
  // fields only exist after LLM finalize. The job-queue entry is the source
  // of truth for live status/progress while generation is in flight.
  // If both exist for the same id, prefer the API album so the rich
  // metadata reaches the row; otherwise fall back to whichever side has it.
  const mergedAlbums = useMemo(() => {
    const albumsById = new Map<string, Album>();
    albums.forEach((album) => albumsById.set(album.id, album));

    const result: Array<Album | AlbumJob> = [];
    const seenIds = new Set<string>();

    albumJobsFromQueue.forEach((job) => {
      const apiAlbum = albumsById.get(job.id);
      // Once the API returns a completed album for this id, the live job
      // queue is no longer needed — switch to the API row so we don't lose
      // album_title / file_size / page_count.
      if (apiAlbum && apiAlbum.status === 'completed') {
        result.push(apiAlbum);
      } else {
        result.push(job);
      }
      seenIds.add(job.id);
    });

    albums.forEach((album) => {
      if (!seenIds.has(album.id)) {
        result.push(album);
      }
    });

    const sorted = result.sort((a, b) => {
      const dateA = 'createdAt' in a ? a.createdAt : a.created_at;
      const dateB = 'createdAt' in b ? b.createdAt : b.created_at;
      return new Date(dateB).getTime() - new Date(dateA).getTime();
    });

    return sorted;
  }, [albumJobsFromQueue, albums]);

  return {
    // State
    highlightedAlbumId,
    selectedLanguage,
    setSelectedLanguage,

    // Plan features
    hasAdvancedAlbumOptions,
    isAlbumTemplatesAvailable,

    // Album settings
    selectedTemplate,
    storedSectionToggles,
    storedSelectedLanguage,
    storedRequirements,
    storedIsExtendedThinking: albumSettings.isExtendedThinking ?? false,
    storedMeasurementSystem: albumSettings.measurementSystem,
    updateSetting,

    // Album operations
    albums,
    mergedAlbums,
    generateAlbum,
    lastGenerateResult,
    resetGenerateResult,
    deleteAlbum,
    downloadAlbum,
    isGenerating,
    isDeleting,
    hasAlbumInQueue,
    albumInQueue,

    // Selected renders
    selectedRenders,
    selectedRenderUrls,

    // Cost-estimate picker (FE4)
    estimatePicker,
  };
}
